import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { dismissListingHeroTransition } from "@/lib/native/listingHeroTransition";
import { useListing } from "@/hooks/useListing";
import { useFavorites } from "@/hooks/useFavorites";
import { ListingDetails } from "@/types/listing";
import EmptyState from "@/components/ui/EmptyState";
import { CarDetailsContent } from "@/components/car-listing/CarDetailsContent";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import { useAuth } from "@/hooks/useAuth";
import { NativeCarDetailsSkeleton } from "@/components/native/NativeCarDetailsSkeleton";
import { buildAuctionListingPath } from "@/lib/listingUrl";

const CarDetails = () => {
  const { listingId, campaignId } = useParams<{
    listingId: string;
    campaignId: string;
  }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const labels = carDetailsLabels(language);
  const { user } = useAuth();
  const { isNative } = useNativePlatform();
  const isGuest = !user;

  const { getListing, getPublicListing } = useListing();

  const userId = user?.id ?? "";
  const authListingQuery = getListing(listingId || "", userId);
  const publicListingQuery = getPublicListing(listingId || "");

  const listingQuery = isGuest ? publicListingQuery : authListingQuery;
  const {
    data: listingData,
    isLoading: isLoadingListing,
    error: listingError,
  } = listingQuery;

  const { addFavorite, removeFavorite } = useFavorites(userId);
  const [listing, setListing] = useState<ListingDetails | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [listingId]);

  useEffect(() => {
    if (!isNative) return;
    if (listingError && !isLoadingListing) {
      dismissListingHeroTransition();
    }
  }, [listingError, isLoadingListing, isNative]);

  useEffect(() => {
    if (listingData?.listing) {
      const raw = listingData.listing as ListingDetails & {
        isGuestView?: boolean;
      };
      setListing({
        ...raw,
        isGuestView: isGuest || raw.isGuestView,
        auctionId: raw.auctionId ?? raw.id,
        isFavorite: raw.isFavorite ?? false,
        isMyListing: raw.isMyListing ?? false,
        sellerId: raw.sellerId ?? "",
        createdById: raw.createdById ?? "",
        bidFee: raw.bidFee ?? null,
        vehicleTest: {
          ...raw.vehicleTest,
          vatCar: Boolean(
            raw.vehicleTest?.vatCar ??
              (raw.vehicleTest as { isVatCar?: boolean } | undefined)?.isVatCar ??
              false,
          ),
        },
      });

      // Soft 301 → canonical /listings/<slug|id>
      const canonical = buildAuctionListingPath(
        raw.campaignId ?? campaignId,
        raw,
      );
      const current = (window.location.pathname || "").replace(/\/+$/, "");
      if (current !== canonical) {
        navigate(canonical, { replace: true });
      }
    }
  }, [listingData, isGuest, listingId, campaignId, navigate]);

  if (isLoadingListing || (!listingData?.listing && !listingError)) {
    if (isNative) {
      return listingId ? (
        <NativeCarDetailsSkeleton listingId={listingId} />
      ) : null;
    }

    return (
      <div className="min-h-screen bg-transparent relative animate-fade-in">
        <div className="container max-w-6xl py-2 px-4 relative z-10">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg">{labels.loading}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!listingData?.listing && listingError) {
    return (
      <EmptyState
        title={labels.notFound.title}
        description={labels.notFound.description.replace(
          "{id}",
          listingId || "",
        )}
        showBackButton={true}
        backUrl={"/auctions"}
      />
    );
  }

  if (!listing || !listingId) {
    return null;
  }

  return (
    <CarDetailsContent
      listingId={listing.id}
      listing={listing}
      userId={userId}
      listingStatus={listing.status}
      isGuestMode={!!listing.isGuestView}
      addFavorite={addFavorite}
      removeFavorite={removeFavorite}
      setListing={setListing}
    />
  );
};

export default CarDetails;
